import { Box, Fade, Grid, Stack, Typography, useTheme } from '@mui/material'
import React, { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnalyticMetric } from '../graphs/analytic-metric'
import { IntensityRatioCards, IntensityRatioMetric } from '../graphs/intensity-ratio-cards'
import AnalyticsService, { IIntensityRatioResult } from '../../../../shared/services/analytics/analytics.service'
import { buildGenericSeriesStack, valueFormatter } from '../../../../shared/interfaces/analytics'
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { isEntityScoped } from '../../../../shared/utils/role-utils'
import { GraphBarChartStacked } from '../graphs/graph-bar-chart-stacked'
import { DrillDownDrawer } from '../drill-down-drawer'
import { useDrillDown } from '../use-drill-down'
import { useDrillDimensionLabel } from '../use-drill-dimension-label'
import { dummyDataStackedScope, dummyDataPreviousBreakdown, dummyDataPreviousPeriodLabel } from '../../../../shared/data/dummy.data'
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay'
import { DashboardSkeleton } from './analytics-overview-skeleton'
import { IDataQualityMix } from '../../../../shared/services/generated-report/generated-report.model'
import { IReportPeriodResponse } from '../../../../shared/services/report-period/report-period.model'

export const AnalyticsOverview: FunctionComponent<{
  reportPeriodId?: string,
  dummyData?: boolean,
  setHasAnalyticsOverviewLoaded?: React.Dispatch<SetStateAction<any>>,
  emissionType: 'marketBased' | 'locationBased',
  // Narrows every figure to one location (the data owner's entity switcher). The BE
  // intersects it with the user's scope, so it can never widen visibility.
  locationId?: string,
  // Rendered beside Total Emissions in the headline row for scoped users (who lose the
  // ratio and UK tiles); without it Total spans the full row.
  companionTile?: React.ReactNode,
  // Explicit "Compare to" period from the analytics page. When set it wins over the automatic
  // previous-period derivation, including on a base year: an explicit choice is honoured even
  // where the automatic comparison is suppressed.
  comparisonPeriodOverride?: IReportPeriodResponse,
}> = ({
  reportPeriodId,
  dummyData = false,
  setHasAnalyticsOverviewLoaded,
  emissionType,
  locationId,
  companionTile,
  comparisonPeriodOverride,
}) => {
    //Services
    const analyticsService = new AnalyticsService()
    //Navigation
    const navigate = useNavigate();
    //Context
    const reportPeriodContext = useContext(ReportPeriodContext);
    const userDetailsContext = useContext(UserDetailsContext);
    // A UK / Non-UK split is an organisation-level view; for an entity-scoped user
    // (one or two sites) it is meaningless, so the tile is hidden.
    const entityScoped = isEntityScoped(userDetailsContext.userDetails);
    //State
    const [stackedEmissions, setStackedEmissions] = useState<any>()
    const [breakdownTotalByEmissionType, setBreakdownTotalByEmissionType] = useState<{ locationBased?: any; marketBased?: any }>({})
    const [prevBreakdownByEmissionType, setPrevBreakdownByEmissionType] = useState<{ locationBased?: any; marketBased?: any }>({})
    const [intensityRatios, setIntensityRatios] = useState<IIntensityRatioResult[]>([])
    const [dataQualityMix, setDataQualityMix] = useState<IDataQualityMix | null>(null)
    const [prevIntensityRatios, setPrevIntensityRatios] = useState<IIntensityRatioResult[]>([])
    //Colors
    const theme = useTheme();
    const colors = getMixedColorPalette(theme.palette.primary.main);
    //Loading
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const showSkeleton = useSkeletonDelay(isLoading, 250);
    //Variables
    const requiredReportPeriodId = reportPeriodId || reportPeriodContext?.lastSelectedReportId;
    // Click-to-drill on the scope history chart. A stale selection clears when the period, basis,
    // comparison or location narrowing changes.
    const scopeTwoMethod = emissionType === 'marketBased' ? 'market' : 'location';
    const drill = useDrillDown([requiredReportPeriodId, emissionType, comparisonPeriodOverride?.id, locationId]);
    const dimLabel = useDrillDimensionLabel();
    const breakdownTotal = breakdownTotalByEmissionType[emissionType];
    // Comparing is opt-in (product call, 2026-09-15): deltas render only when the page
    // passes a chosen comparison period; there is no automatic previous-period fallback.
    const previousPeriod = comparisonPeriodOverride;
    const prevBreakdownTotal = previousPeriod ? prevBreakdownByEmissionType[emissionType] : undefined;
    const prevLabel = previousPeriod ? `vs ${previousPeriod.friendlyName}` : undefined;

    useEffect(() => {
      if (dummyData) {
        setIsLoading(false);
        return;
      }
      if (!requiredReportPeriodId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      let completedRequests = 0;
      const checkLoadingStatus = () => {
        completedRequests += 1;
        if (completedRequests === 2) {
          setIsLoading(false);
          if (setHasAnalyticsOverviewLoaded) setHasAnalyticsOverviewLoaded(true);
        }
      };

      analyticsService
        .getStackedData(requiredReportPeriodId, `?data=scope${locationId ? `&locationId=${locationId}` : ''}`)
        .then((response) => {
          setStackedEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          checkLoadingStatus();
        });

      analyticsService
        .getBreakdownTotal(requiredReportPeriodId, undefined, undefined, locationId)
        .then((response) => {
          setBreakdownTotalByEmissionType({
            locationBased: response.locationBased,
            marketBased: response.marketBased,
          });
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          checkLoadingStatus();
        });
    }, [requiredReportPeriodId, dummyData, locationId]);

    useEffect(() => {
      if (dummyData) return;
      // Clear first so a change of comparison period (the "Compare to" selector) never shows the
      // old period's deltas under the new period's label while the new figures are in flight.
      setPrevBreakdownByEmissionType({});
      if (!previousPeriod?.id) return;
      analyticsService
        .getBreakdownTotal(previousPeriod.id, undefined, undefined, locationId)
        .then((response) => {
          setPrevBreakdownByEmissionType({
            locationBased: response.locationBased,
            marketBased: response.marketBased,
          });
        })
        .catch((err) => {
          console.log(err);
        });
    }, [previousPeriod?.id, dummyData, locationId]);

    // The data-quality mix is supplementary, fetched separately so it never gates the
    // main overview loading state.
    useEffect(() => {
      if (dummyData || !requiredReportPeriodId) return;
      analyticsService
        .getDataQualityMix(requiredReportPeriodId)
        .then(setDataQualityMix)
        .catch((err) => console.log(err));
    }, [requiredReportPeriodId, dummyData]);

    //Share of the reported footprint (on the displayed basis) in the High and Medium
    //data-quality tiers: the upgrade-to-activity-data signal.
    const qualityBucket = dataQualityMix ? (emissionType === 'marketBased' ? dataQualityMix.totalMarket : dataQualityMix.totalLocation) : null;
    const activitySharePct = qualityBucket && qualityBucket.totalTco2e > 0
      ? ((qualityBucket.byTier['High'] ?? 0) + (qualityBucket.byTier['Medium'] ?? 0)) / qualityBucket.totalTco2e * 100
      : null;
    const spendSharePct = qualityBucket && qualityBucket.totalTco2e > 0
      ? (qualityBucket.spendBasedTco2e / qualityBucket.totalTco2e) * 100
      : null;

    // Intensity ratios are supplementary (driven by the settings catalog), fetched
    // separately so they don't gate the main overview loading state.
    useEffect(() => {
      if (dummyData || !requiredReportPeriodId) return;
      analyticsService
        .getIntensityRatios(requiredReportPeriodId)
        .then(setIntensityRatios)
        .catch((err) => console.log(err));
    }, [requiredReportPeriodId, dummyData]);

    useEffect(() => {
      setPrevIntensityRatios([]);
      if (dummyData || !previousPeriod?.id) return;
      analyticsService
        .getIntensityRatios(previousPeriod.id)
        .then(setPrevIntensityRatios)
        .catch((err) => console.log(err));
    }, [previousPeriod?.id, dummyData]);

    // Tile definitions shared by the standard grid and the data-owner two-column layout.
    const totalTile = breakdownTotal ? (
      <AnalyticMetric title='Total Emissions' tooltip='The total greenhouse gas emissions in tCO2e for the selected report period' value={breakdownTotal.total} units='tCO2e'
        comparison={prevBreakdownTotal ? breakdownTotal.total - prevBreakdownTotal.total : undefined}
        comparisonLabel={prevLabel}
        onClick={() => navigate('/emissions/overview/table')} />
    ) : null;
    const scopeTiles = breakdownTotal ? (
      <>
        <AnalyticMetric title='Scope 1 Emissions' tooltip="Direct greenhouse gas emissions from owned or controlled sources, such as company vehicles and on-site fuel combustion. These emissions are directly generated by your organisation's activities." value={breakdownTotal.scope1} units='tCO2e'
          comparison={prevBreakdownTotal ? breakdownTotal.scope1 - prevBreakdownTotal.scope1 : undefined}
          comparisonLabel={prevLabel}
          onClick={() => navigate('/dashboard/scope/1')} />
        <AnalyticMetric title='Scope 2 Emissions' tooltip="Indirect emissions from the generation of purchased electricity, steam, heating, and cooling consumed by your organisation. Although these emissions occur off-site, they are a result of your organisation's energy use." value={breakdownTotal.scope2} units='tCO2e'
          comparison={prevBreakdownTotal ? breakdownTotal.scope2 - prevBreakdownTotal.scope2 : undefined}
          comparisonLabel={prevLabel}
          onClick={() => navigate('/dashboard/scope/2')} />
        <AnalyticMetric title='Scope 3 Emissions' tooltip="Indirect emissions that occur throughout the lifecyle of your organisation's activities, including upstream activities like purchased goods and services, as well as downstream activities such as product use and disposal. These emissions are often the largest part of an organisation's carbon footprint but are not directly controlled by it." value={breakdownTotal.scope3} units='tCO2e'
          comparison={prevBreakdownTotal ? breakdownTotal.scope3 - prevBreakdownTotal.scope3 : undefined}
          comparisonLabel={prevLabel}
          onClick={() => navigate('/dashboard/scope/3')} />
      </>
    ) : null;
    const historyChartTile = breakdownTotal && stackedEmissions ? (
      <GraphBarChartStacked title='Emission History for Scopes' tooltip='The greenhouse gas emission history for each month in the selected report period per scope. Click a scope to drill in.'
        dataset={stackedEmissions[emissionType]}
        series={buildGenericSeriesStack(stackedEmissions[emissionType], valueFormatter)}
        colors={colors}
        onSelect={(seriesKey) => {
          // The scope series are labelled "Scope 1/2/3"; the drill filters on the bare digit.
          const id = seriesKey.replace(/^Scope\s*/i, '').trim();
          if (id) drill.openDrill({ dimension: 'scope', id, label: seriesKey });
        }}
      />
    ) : null;
    const dataQualityTile = activitySharePct !== null ? (
      <AnalyticMetric
        title='Data Quality'
        tooltip={`The share of your reported footprint calculated from actual or estimated activity data rather than spend or assumptions. ${spendSharePct !== null && spendSharePct > 0 ? `${spendSharePct.toFixed(0)}% of your footprint is currently spend-based: replacing spend entries with activity data (meter readings, fuel volumes, supplier figures) improves accuracy.` : 'Replacing spend entries with activity data (meter readings, fuel volumes, supplier figures) improves accuracy.'}`}
        value={Math.round(activitySharePct)}
        units='% activity data'
        onClick={() => navigate('/analytics/hotspots')} />
    ) : null;
    // Data-owner dashboard: two independent columns so each card keeps its natural height
    // and the left column packs tightly under Total (no row-stretch white space).
    const scopedTwoColumn = entityScoped && !!companionTile;

    return (
      <>
        {
          isLoading && (
            <Fade in={showSkeleton} unmountOnExit>
              <div>
                <DashboardSkeleton />
              </div>
            </Fade>
          )
        }
        {
          !isLoading && (
            <Fade in={!showSkeleton} unmountOnExit>
              <div>
                <Grid container spacing={2}>
                  {breakdownTotal && !dummyData && scopedTwoColumn &&
                    <>
                      <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                        <Stack spacing={2}>
                          {totalTile}
                          {scopeTiles}
                          {dataQualityTile}
                        </Stack>
                      </Grid>
                      <Grid item xl={8} lg={8} md={6} sm={12} xs={12}>
                        <Stack spacing={2}>
                          {companionTile}
                          {historyChartTile}
                        </Stack>
                      </Grid>
                    </>
                  }
                  {breakdownTotal && !dummyData && !scopedTwoColumn &&
                    <>
                      {/* Entity-scoped sessions get perEmployee: null from the BE (an org-wide
                          denominator against an entity numerator is meaningless), so the middle
                          intensity/per-employee tile is hidden. When it is, lay the two remaining
                          headline cards out two-up so the row fills cleanly instead of stretching
                          to the height of the Scope 1/2/3 stack beside it. */}
                      {(() => {
                      const showRatioTile = intensityRatios.length > 0 || breakdownTotal.perEmployee != null;
                      // Scoped users (without a companion tile) also lose the UK/Non-UK tile,
                      // leaving Total as the full-width headline.
                      const showUkTile = !entityScoped;
                      const headlineCols = showRatioTile ? 4 : (showUkTile ? 6 : 12);
                      return (
                      <>
                      <Grid item xl={headlineCols} lg={headlineCols} md={showUkTile ? 6 : 12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                        {totalTile}
                      </Grid>
                      {showRatioTile &&
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                          {intensityRatios.length > 0
                            ? <IntensityRatioMetric ratio={intensityRatios[0]} previousRatios={prevIntensityRatios} comparisonLabel={prevLabel} />
                            : <AnalyticMetric title='Emissions per Employee' value={breakdownTotal.perEmployee} units='tCO2e'
                              comparison={prevBreakdownTotal && prevBreakdownTotal.perEmployee != null ? breakdownTotal.perEmployee - prevBreakdownTotal.perEmployee : undefined}
                              comparisonLabel={prevLabel} />
                          }
                        </Grid>
                      }
                      {showUkTile &&
                        <Grid item xl={headlineCols} lg={headlineCols} md={showRatioTile ? 12 : 6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <AnalyticMetric units='tCO2e' title='UK Emissions' titleTwo='Non UK Emissions' value={breakdownTotal.ukTotal} valueTwo={breakdownTotal.nonUkTotal}
                            toggleOne='UK' toggleTwo='Non UK'
                            comparison={prevBreakdownTotal ? breakdownTotal.ukTotal - prevBreakdownTotal.ukTotal : undefined}
                            comparisonTwo={prevBreakdownTotal ? breakdownTotal.nonUkTotal - prevBreakdownTotal.nonUkTotal : undefined}
                            comparisonLabel={prevLabel} />
                        </Grid>
                      }
                      </>
                      )})()}
                    </>
                  }
                  {breakdownTotal && !dummyData && !scopedTwoColumn &&
                    <Grid item xl={4} lg={4} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'space-around' }}>
                        {scopeTiles}
                      </Box>
                    </Grid>}
                  {breakdownTotal && stackedEmissions && !dummyData && !scopedTwoColumn &&
                    <Grid item xl={8} lg={8} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                      {historyChartTile}
                    </Grid>
                  }
                  {!dummyData && activitySharePct !== null && !scopedTwoColumn &&
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                      {dataQualityTile}
                    </Grid>
                  }
                  {/* All enabled intensity ratios (from the settings catalog) as their own labelled
                      section so they wrap uniformly regardless of count. The top ratio is also
                      featured in the headline row above. */}
                  {!dummyData && intensityRatios.length > 0 &&
                    <>
                      <Grid item xs={12}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 600, mt: 2 }}>Intensity ratios</Typography>
                      </Grid>
                      <IntensityRatioCards
                        ratios={intensityRatios}
                        previousRatios={prevIntensityRatios}
                        comparisonLabel={prevLabel}
                      />
                    </>
                  }
                  {dummyData &&
                    <>
                      <Grid item xl={4} lg={4} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <AnalyticMetric title='Total Emissions' tooltip='The total greenhouse gas emissions in tCO2e for the selected report period' value={19.16} units='tCO2e'
                          comparison={19.16 - dummyDataPreviousBreakdown.total}
                          comparisonLabel={`vs ${dummyDataPreviousPeriodLabel}`} />
                      </Grid>
                      <Grid item xl={4} lg={4} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <AnalyticMetric title='Emissions per Employee' value={0.6} units='tCO2e'
                          comparison={0.6 - dummyDataPreviousBreakdown.perEmployee}
                          comparisonLabel={`vs ${dummyDataPreviousPeriodLabel}`} />
                      </Grid>
                      <Grid item xl={4} lg={4} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <AnalyticMetric units='tCO2e' title='UK Emissions' titleTwo='Non UK Emissions' value={12.83} valueTwo={6.33}
                          toggleOne='UK' toggleTwo='Non UK'
                          comparison={12.83 - dummyDataPreviousBreakdown.ukTotal}
                          comparisonTwo={6.33 - dummyDataPreviousBreakdown.nonUkTotal}
                          comparisonLabel={`vs ${dummyDataPreviousPeriodLabel}`} />
                      </Grid>
                      <Grid item xl={4} lg={4} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'space-around' }}>
                          <AnalyticMetric title='Scope 1 Emissions' tooltip="Direct greenhouse gas emissions from owned or controlled sources, such as company vehicles and on-site fuel combustion. These emissions are directly generated by your organisation's activities." value={4.25} units='tCO2e'
                            comparison={4.25 - dummyDataPreviousBreakdown.scope1}
                            comparisonLabel={`vs ${dummyDataPreviousPeriodLabel}`} />
                          <AnalyticMetric title='Scope 2 Emissions' tooltip="Indirect emissions from the generation of purchased electricity, steam, heating, and cooling consumed by your organisation. Although these emissions occur off-site, they are a result of your organisation's energy use." value={3.41} units='tCO2e'
                            comparison={3.41 - dummyDataPreviousBreakdown.scope2}
                            comparisonLabel={`vs ${dummyDataPreviousPeriodLabel}`} />
                          <AnalyticMetric title='Scope 3 Emissions' tooltip="Indirect emissions that occur throughout the lifecyle of your organisation's activities, including upstream activities like purchased goods and services, as well as downstream activities such as product use and disposal. These emissions are often the largest part of an organisation's carbon footprint but are not directly controlled by it." value={11.50} units='tCO2e'
                            comparison={11.50 - dummyDataPreviousBreakdown.scope3}
                            comparisonLabel={`vs ${dummyDataPreviousPeriodLabel}`} />
                        </Box>
                      </Grid>
                      <Grid item xl={8} lg={8} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <GraphBarChartStacked title='Emission History for Scopes' tooltip='The greenhouse gas emission history for each month in the selected report period per scope'
                          dataset={dummyDataStackedScope}
                          colors={colors}
                          series={buildGenericSeriesStack(dummyDataStackedScope, valueFormatter)} />
                      </Grid>
                    </>}
                </Grid >
              </div>
            </Fade>
          )}

        <DrillDownDrawer
          open={drill.open}
          onClose={drill.close}
          selection={drill.selection}
          reportPeriodId={requiredReportPeriodId || ''}
          scopeTwoMethod={scopeTwoMethod}
          comparisonReportPeriodId={comparisonPeriodOverride?.id}
          dimensionLabel={dimLabel(drill.selection?.dimension)}
        />
      </>
    )
  }